import type {
  HeaderConfigDTO,
  HeaderOrderDTO,
  HeaderOrderUpsertDTO,
  TimelineLayoutDTO,
  upsertTimelineLayoutDTOSchema,
} from '@tempo-io/roadmap-dto';
import { HTTPError } from 'ky';
import type { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';

type TimelineLayoutUpsertDTO = z.infer<typeof upsertTimelineLayoutDTOSchema>;

const fetchTimelineLayout = async (
  viewId: number,
  visualizationId: number,
): Promise<TimelineLayoutDTO | null> => {
  try {
    const response = await tempoApi()
      .get(
        `api/v1/loop-visualization/view/${viewId}/visualization/${visualizationId}/timeline-layout`,
      )
      .json<TimelineLayoutDTO>();
    return response;
  } catch (err: unknown) {
    if (err instanceof HTTPError && err.response.status === 404) {
      return null;
    }
    throw err;
  }
};

const upsertTimelineLayout = async (
  viewId: number,
  visualizationId: number,
  layout: TimelineLayoutUpsertDTO,
): Promise<TimelineLayoutDTO> => {
  return tempoApi()
    .put(
      `api/v1/loop-visualization/view/${viewId}/visualization/${visualizationId}/timeline-layout`,
      {
        json: layout,
      },
    )
    .json<TimelineLayoutDTO>();
};

const createTimelineLayoutWithSmartDefaults = async (
  viewId: number,
  visualizationId: number,
): Promise<TimelineLayoutDTO | null> => {
  const response = await tempoApi().post(
    `api/v1/loop-visualization/view/${viewId}/visualization/${visualizationId}/timeline-layout`,
  );
  if (response.status === 204) {
    return null;
  }
  return response.json<TimelineLayoutDTO>();
};

const fetchHeaderConfig = async ({
  viewId,
  visualizationId,
  headerFieldId,
  parentFieldId = null,
  parentHeaderId = null,
}: {
  viewId: number;
  visualizationId: number;
  headerFieldId: string;
  parentFieldId?: string | null;
  parentHeaderId?: string | null;
}): Promise<HeaderConfigDTO> => {
  try {
    const searchParams = new URLSearchParams();
    if (parentFieldId) {
      searchParams.set('parentFieldId', parentFieldId);
    }
    if (parentHeaderId) {
      searchParams.set('parentHeaderId', parentHeaderId);
    }

    const url = `api/v1/loop-visualization/view/${viewId}/visualization/${visualizationId}/timeline-layout/header-config/${headerFieldId}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    const response = await tempoApi().get(url).json<HeaderConfigDTO>();
    return response;
  } catch (err: unknown) {
    if (err instanceof HTTPError && err.response.status === 404) {
      return {
        headerFieldId,
        parentFieldHeaderId: parentFieldId,
        parentHeaderId,
        orderedHeaderIds: [],
        hiddenHeaderIds: [],
      };
    }
    throw err;
  }
};

const updateHeaderOrder = async (
  viewId: number,
  visualizationId: number,
  headerFieldId: string,
  headerOrder: HeaderOrderUpsertDTO,
): Promise<HeaderOrderDTO> => {
  return tempoApi()
    .put(
      `api/v1/loop-visualization/view/${viewId}/visualization/${visualizationId}/timeline-layout/header-order/${headerFieldId}`,
      {
        json: headerOrder,
      },
    )
    .json<HeaderOrderDTO>();
};

const hideHeader = async (
  viewId: number,
  visualizationId: number,
  headerFieldId: string,
  headerId: string,
  parentFieldHeaderId: string | null = null,
  parentHeaderId: string | null = null,
): Promise<void> => {
  await tempoApi().post(
    `api/v1/loop-visualization/view/${viewId}/visualization/${visualizationId}/timeline-layout/hidden-headers/${headerFieldId}`,
    {
      json: { headerId, parentFieldHeaderId, parentHeaderId },
    },
  );
};

const showHeader = async (
  viewId: number,
  visualizationId: number,
  headerFieldId: string,
  headerId: string,
  parentFieldHeaderId: string | null = null,
  parentHeaderId: string | null = null,
): Promise<void> => {
  const searchParams = new URLSearchParams();
  if (parentFieldHeaderId) searchParams.set('parentFieldHeaderId', parentFieldHeaderId);
  if (parentHeaderId) searchParams.set('parentHeaderId', parentHeaderId);
  const queryString = searchParams.toString();
  const url = `api/v1/loop-visualization/view/${viewId}/visualization/${visualizationId}/timeline-layout/hidden-headers/${headerFieldId}/${headerId}${queryString ? `?${queryString}` : ''}`;
  await tempoApi().delete(url);
};

export const TimelineLayoutApi = {
  fetchTimelineLayout,
  upsertTimelineLayout,
  createTimelineLayoutWithSmartDefaults,
  fetchHeaderConfig,
  updateHeaderOrder,
  hideHeader,
  showHeader,
} as const;
